/**
 * 初期表示位置の決定（design §11-2 / 要件18）。
 *
 * 優先順:
 *   1. ?focus=<user_id> があればその参加者へ（呼び出し側で users から座標解決）
 *   2. geolocation が既に許可済みなら現在地
 *   3. 公開参加者がいれば fitBounds
 *   4. どれもなければ既定（ラスベガス）
 *
 * 位置情報の許可は強制しない（既に granted のときだけ getCurrentPosition）。
 */
import type { Map as MaplibreMap } from 'maplibre-gl';
import type { UserLocation } from '../api/client';

export const LAS_VEGAS_CENTER: [number, number] = [-115.17, 36.1];
export const DEFAULT_ZOOM = 12;
/** ?lat/lng や ?focus のフォーカス時の既定ズーム。 */
export const FOCUS_ZOOM = 15;

/** URL の ?focus= を読む。 */
export function getFocusUserId(): string | null {
  const params = new URLSearchParams(window.location.search);
  return params.get('focus');
}

/**
 * URL の ?lat=&lng=(&zoom=) を読む。API を待たず地図初期化時に使う（要件18-6）。
 * 値が不正・欠落なら null。
 */
export function getInitialLatLng(): {
  center: [number, number];
  zoom: number;
} | null {
  const params = new URLSearchParams(window.location.search);
  const latRaw = params.get('lat');
  const lngRaw = params.get('lng');
  // 両方のパラメータが存在しなければ無効（Number(null)=0 の誤判定を防ぐ）
  if (latRaw === null || lngRaw === null) {
    return null;
  }
  const lat = Number(latRaw);
  const lng = Number(lngRaw);
  if (
    !Number.isFinite(lat) ||
    !Number.isFinite(lng) ||
    lat < -90 ||
    lat > 90 ||
    lng < -180 ||
    lng > 180
  ) {
    return null;
  }
  const zoomRaw = Number(params.get('zoom'));
  const zoom = Number.isFinite(zoomRaw) && zoomRaw > 0 ? zoomRaw : FOCUS_ZOOM;
  return { center: [lng, lat], zoom };
}

/** geolocation が既に許可済みかを調べる（プロンプトは出さない）。 */
async function isGeolocationGranted(): Promise<boolean> {
  if (!('permissions' in navigator) || !navigator.geolocation) {
    return false;
  }
  try {
    const status = await navigator.permissions.query({ name: 'geolocation' });
    return status.state === 'granted';
  } catch {
    return false;
  }
}

/** 現在地を取得して中心にする。 */
function centerOnCurrentPosition(map: MaplibreMap): Promise<boolean> {
  return new Promise((resolve) => {
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        map.easeTo({
          center: [pos.coords.longitude, pos.coords.latitude],
          zoom: 14,
        });
        resolve(true);
      },
      () => resolve(false),
      { timeout: 5000 },
    );
  });
}

/**
 * 初期カメラを決める。map load 後・最初の users 取得後に一度だけ呼ぶ。
 * @returns 適用した種別（デバッグ用）
 */
export async function applyInitialView(
  map: MaplibreMap,
  users: UserLocation[],
): Promise<'latlng' | 'focus' | 'geolocation' | 'default'> {
  // 0. ?lat/lng は地図初期化時に既に適用済み。ここでは他ロジックで上書きしない。
  if (getInitialLatLng()) {
    return 'latlng';
  }

  // 1. ?focus=（閲覧者が明示指定した特定ユーザー）
  const focusId = getFocusUserId();
  if (focusId) {
    const target = users.find((u) => u.user_id === focusId);
    if (target) {
      map.easeTo({ center: [target.latest.lng, target.latest.lat], zoom: 15 });
      return 'focus';
    }
  }

  // 2. geolocation 許可済みなら現在地
  if (await isGeolocationGranted()) {
    if (await centerOnCurrentPosition(map)) {
      return 'geolocation';
    }
  }

  // 3. 既定（ラスベガス）。参加者への自動フィットはしない（要件18-3）。
  //    参加者を見るのはアコーディオンからの選択で行う。
  map.easeTo({ center: LAS_VEGAS_CENTER, zoom: DEFAULT_ZOOM });
  return 'default';
}
